import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Coffee } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const HERO = "https://images.unsplash.com/photo-1515823662972-da6a2e4d3002?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwzfHxhcnRpc2FuJTIwY29mZmVlJTIwcGFzdHJ5JTIwY2FmZSUyMGZvb2QlMjByZXN0b2JhcnxlbnwwfHx8fDE3ODg1OTkyODJ8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { setUser } = useAuth();
  const { t, lang, setLang } = useLang();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [resetForm, setResetForm] = useState({ token: "", password: "" });
  const [resetLink, setResetLink] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const tok = new URLSearchParams(window.location.search).get("token");
    if (tok) {
      setMode("reset");
      setResetForm((f) => ({ ...f, token: tok }));
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = mode === "login"
        ? await api.post("/auth/login", { email: form.email, password: form.password })
        : await api.post("/auth/register", form);
      setUser(res.data);
      navigate("/");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.post("/auth/forgot-password", { email: form.email });
      if (res.data?.reset_token) {
        setResetLink(`${window.location.origin}/login?token=${res.data.reset_token}`);
        setResetForm({ token: res.data.reset_token, password: "" });
      } else {
        toast.info(t("resetSent"));
      }
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/reset-password", resetForm);
      toast.success(t("resetSuccess"));
      setMode("login");
      setResetLink("");
      window.history.replaceState({}, "", "/login");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const titles = { login: t("welcome"), register: t("register"), forgot: t("forgotPassword"), reset: t("resetPassword") };

  return (
    <div className="min-h-screen flex bg-[#090D16]" data-testid="login-page">
      <div className="hidden lg:block lg:w-[55%] relative">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-[#090D16]" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-11 h-11 rounded-xl bg-amber-600 flex items-center justify-center"><Coffee className="w-6 h-6" /></div>
            <div className="font-heading font-bold text-xl">KQZ Business</div>
          </div>
          <h1 className="font-heading text-4xl xl:text-5xl font-extrabold leading-tight mb-3">{t("appName")}</h1>
          <p className="text-slate-300 text-lg max-w-md">{t("tagline")}</p>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="flex items-center justify-between mb-8">
            <div className="lg:hidden flex items-center gap-2 text-white">
              <div className="w-9 h-9 rounded-xl bg-amber-600 flex items-center justify-center"><Coffee className="w-5 h-5" /></div>
              <span className="font-heading font-bold">KQZ Business</span>
            </div>
            <button data-testid="login-lang-toggle" onClick={() => setLang(lang === "id" ? "en" : "id")} className="text-xs font-mono font-semibold border border-slate-700 text-slate-300 rounded-full px-3 py-1.5 hover:bg-slate-800 ml-auto">
              {lang === "id" ? "ID" : "EN"}
            </button>
          </div>
          <h2 className="font-heading text-2xl font-bold text-white mb-1">{titles[mode]}</h2>
          <p className="text-sm text-slate-400 mb-6">Recipe Costing · POS · Inventory · Finance</p>

          {(mode === "login" || mode === "register") && (
            <form onSubmit={submit} className="space-y-4">
              {mode === "register" && (
                <div>
                  <Label className="text-slate-300 text-xs uppercase tracking-wider">{t("name")}</Label>
                  <Input data-testid="register-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1.5 bg-slate-900 border-slate-700 text-white" />
                </div>
              )}
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-wider">{t("email")}</Label>
                <Input data-testid="login-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="mt-1.5 bg-slate-900 border-slate-700 text-white" />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-slate-300 text-xs uppercase tracking-wider">{t("password")}</Label>
                  {mode === "login" && (
                    <button type="button" data-testid="forgot-password-link" onClick={() => { setMode("forgot"); setResetLink(""); }} className="text-xs text-amber-500 hover:text-amber-400">
                      {t("forgotPassword")}
                    </button>
                  )}
                </div>
                <Input data-testid="login-password-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required className="mt-1.5 bg-slate-900 border-slate-700 text-white" />
              </div>
              <Button data-testid="login-submit-btn" type="submit" disabled={busy} className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold py-2.5">
                {busy ? t("loading") : mode === "login" ? t("login") : t("register")}
              </Button>
            </form>
          )}

          {mode === "forgot" && (
            <form onSubmit={submitForgot} className="space-y-4">
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-wider">{t("email")}</Label>
                <Input data-testid="forgot-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="mt-1.5 bg-slate-900 border-slate-700 text-white" />
              </div>
              <Button data-testid="forgot-submit-btn" type="submit" disabled={busy} className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold py-2.5">
                {busy ? t("loading") : t("sendReset")}
              </Button>
              {resetLink && (
                <div className="bg-slate-900 border border-amber-800 rounded-lg p-3 space-y-2" data-testid="reset-link-box">
                  <p className="text-xs text-amber-400">{t("resetLinkReady")}</p>
                  <p className="text-xs font-mono text-slate-300 break-all">{resetLink}</p>
                  <Button type="button" data-testid="goto-reset-btn" onClick={() => setMode("reset")} className="w-full bg-amber-600 hover:bg-amber-700 text-white text-sm">
                    {t("resetPassword")} →
                  </Button>
                </div>
              )}
            </form>
          )}

          {mode === "reset" && (
            <form onSubmit={submitReset} className="space-y-4">
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-wider">Token</Label>
                <Input data-testid="reset-token-input" value={resetForm.token} onChange={(e) => setResetForm({ ...resetForm, token: e.target.value })} required className="mt-1.5 bg-slate-900 border-slate-700 text-white font-mono text-xs" />
              </div>
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-wider">{t("newPassword")}</Label>
                <Input data-testid="reset-password-input" type="password" value={resetForm.password} onChange={(e) => setResetForm({ ...resetForm, password: e.target.value })} required minLength={6} className="mt-1.5 bg-slate-900 border-slate-700 text-white" />
              </div>
              <Button data-testid="reset-submit-btn" type="submit" disabled={busy} className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold py-2.5">
                {busy ? t("loading") : t("resetPassword")}
              </Button>
            </form>
          )}

          {(mode === "login" || mode === "register") && (
            <>
              <div className="flex items-center gap-3 my-5">
                <div className="flex-1 border-t border-slate-800" />
                <span className="text-xs text-slate-500">atau / or</span>
                <div className="flex-1 border-t border-slate-800" />
              </div>
              <Button data-testid="google-login-btn" variant="outline" onClick={googleLogin} className="w-full border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800">
                <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#EA4335" d="M12 5.04c1.7 0 3.22.59 4.42 1.74l3.29-3.29C17.72 1.64 15.06.5 12 .5 7.4.5 3.44 3.13 1.53 6.66l3.83 2.97C6.3 7.02 8.9 5.04 12 5.04z"/><path fill="#4285F4" d="M23.5 12.27c0-.85-.08-1.66-.22-2.45H12v4.64h6.45c-.28 1.48-1.12 2.73-2.38 3.57l3.72 2.89c2.18-2.01 3.71-4.98 3.71-8.65z"/><path fill="#FBBC05" d="M5.36 14.37a7.06 7.06 0 0 1 0-4.47L1.53 6.93a11.5 11.5 0 0 0 0 10.41l3.83-2.97z"/><path fill="#34A853" d="M12 23.5c3.06 0 5.63-1.01 7.5-2.74l-3.72-2.89c-1 .68-2.29 1.08-3.78 1.08-3.1 0-5.7-1.98-6.64-4.73l-3.83 2.97C3.44 20.87 7.4 23.5 12 23.5z"/></svg>
                {t("loginGoogle")}
              </Button>
            </>
          )}

          <p className="text-sm text-slate-400 text-center mt-6">
            {mode === "login" && (
              <>{t("noAccount")}{" "}
                <button data-testid="toggle-auth-mode-btn" onClick={() => setMode("register")} className="text-amber-500 hover:text-amber-400 font-medium">{t("register")}</button>
              </>
            )}
            {mode === "register" && (
              <>{t("haveAccount")}{" "}
                <button data-testid="toggle-auth-mode-btn" onClick={() => setMode("login")} className="text-amber-500 hover:text-amber-400 font-medium">{t("login")}</button>
              </>
            )}
            {(mode === "forgot" || mode === "reset") && (
              <button data-testid="back-to-login-btn" onClick={() => { setMode("login"); setResetLink(""); }} className="text-amber-500 hover:text-amber-400 font-medium">
                ← {t("backToLogin")}
              </button>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
